/**
 * Product Utils
 * ----------------------------------
 * A unified class for managing product functionality including:
 * - Subscription handling: pricing, quantities, and purchase options
 * - Tab management: switching between one-time purchase and subscription options
 * - Form handling: form submission and cart interactions
 */

import { updateFreshpackText, updatePrice } from './product-card-helpers';
import { addItemsToCart } from './cart-utils';

/**
 * Unified manager for product functionality including subscriptions,
 * tab switching, and form handling
 */
export class ProductManager {
  /**
   * @param {HTMLElement} element - The product component element
   */
  constructor(element) {
    this.initVariables(element);
    this.addEventListeners();
    this.initTabs();
    this.setInitialSubscriptionOption();
    updateFreshpackText(this.element, this.element.quantity);
  }

  /**
   * Initialize class variables and DOM elements
   * @param {HTMLElement} element - The product component element
   * @private
   */
  initVariables(element) {
    this.element = element;
    this.lastSellingPlan = undefined;
    this.subscriptionOnlyProduct =
      element.dataset.subscriptionOnlyProduct === 'true';

    this.productPriceElements = element.querySelectorAll(
      '[data-product-total-price]'
    );
    this.productUnitPriceElements = element.querySelectorAll(
      '[data-product-unit-price]'
    );

    this.allProductPriceElements = element.querySelectorAll(
      '[data-product-total-price], [data-product-unit-price]'
    );

    this.addToCartButton = element.querySelector('[data-add-to-cart]');
    this.sellingPlanInput = element.querySelectorAll(
      '[data-selling-plan-input]'
    );
    this.element.quantity = 1;
    this.formElement = this.element.querySelector('form');
    this.variantId = this.element.querySelector('[data-variant-id]')?.value;
    this.tabs = element.querySelectorAll('[data-tab]');
    this.selfInstallationConfirmationInput = element.querySelector(
      '[data-self-installation-confirmation]'
    );

    this.distributorInstallationButton = element.querySelector(
      '[data-distributor-installation-button]'
    );

    this.subscriptionCaption = this.element.querySelector(
      '[data-subscription-caption]'
    );
  }

  /**
   * Initialize all event listeners
   * @private
   */
  addEventListeners() {
    // Subscription quantity event listener
    this.element.addEventListener('pxl:quantityChange', event => {
      this.element.quantity = event.detail.quantity;
      updateFreshpackText(this.element, this.element.quantity);
      updatePrice(this.element.quantity, this.productPriceElements);
    });

    // Selling plan change event listeners
    for (const input of this.sellingPlanInput) {
      input.addEventListener('change', () => {
        this.setCurrentSubscriptionPrice();
      });
    }

    // Form submission event listeners
    if (this.formElement) {
      this.formElement.addEventListener('submit', event => {
        event.preventDefault();
        const form = event.target;
        const formData = new FormData(form);
        formData.set('quantity', this.element.quantity);
        const ctaButton = event.target.querySelector('[data-add-to-cart]');
        addItemsToCart(formData, ctaButton);
      });

      this.formElement.addEventListener('change', event => {
        if (event.target.closest('[data-option]')) {
          this.element.onOptionChange(event);
        }
      });
    }

    this.selfInstallationConfirmationInput.addEventListener('change', () => {
      if (!this.addToCartButton) return;

      this.addToCartButton.disabled =
        !this.selfInstallationConfirmationInput.checked;
    });
  }

  /**
   * Initialize tab click event listeners
   * @private
   */
  initTabs() {
    for (const tab of this.tabs) {
      tab.addEventListener('click', event => {
        event.preventDefault();

        this.handleTabChange(event.currentTarget.dataset.tab);
      });
    }
  }

  /**
   * Handles tab change events by updating active states and showing appropriate content
   * @param {string} tabId - The ID of the tab to activate ('one-time' or 'subscribe')
   * @private
   */
  handleTabChange(tabId) {
    const targetTab = this.element.querySelector(`[data-tab="${tabId}"]`);
    if (!targetTab) return;

    for (const otherTab of this.tabs) {
      delete otherTab.dataset.active;
    }

    targetTab.dataset.active = '';
    this.showTab(tabId);
  }

  /**
   * Shows the specified tab content and hides others
   * @param {string} tabId - ID of the tab to show
   * @private
   */
  showTab(tabId) {
    for (const tab of this.element.querySelectorAll('[data-tab-content]')) {
      delete tab.dataset.active;
    }

    const targetTab = this.element.querySelector(
      `[data-tab-content="${tabId}"]`
    );

    if (targetTab) {
      targetTab.dataset.active = '';
    }

    switch (tabId) {
      case 'one-time': {
        this.afterOneTimePurchaseSelected();

        break;
      }
      case 'subscribe': {
        this.afterSubscriptionPurchaseSelected();

        break;
      }
      case 'distributor-installation': {
        this.afterDistributorInstallationSelected();

        break;
      }
      case 'self-installation': {
        this.afterSelfInstallationSelected();

        break;
      }
    }
  }

  /**
   * Sets the initial subscription option when the component loads
   * For subscription-only products, checks the first option and sets price
   * @private
   */
  setInitialSubscriptionOption() {
    const input = this.element.querySelectorAll(
      'input[name="selling_plan"]'
    )[0];

    if (!input) return;

    if (this.subscriptionOnlyProduct) {
      input.checked = true;
      this.setCurrentSubscriptionPrice();
    }

    this.lastSellingPlan = input.value;
  }

  /**
   * Sets the subscription quantity from the subscription quantity input
   * @private
   */
  setSubscriptionQuantity() {
    if (!this.element.querySelector('[data-subscription-quantity] input'))
      return;

    this.element.quantity =
      Number.parseInt(
        this.element.querySelector('[data-subscription-quantity] input').value
      ) || 1;
  }

  /**
   * Sets the one-time purchase quantity from the one-time quantity input
   * @private
   */
  setOneTimeQuantity() {
    if (!this.element.querySelector('[data-one-time-quantity] input')) return;

    this.element.quantity =
      Number.parseInt(
        this.element.querySelector('[data-one-time-quantity] input').value
      ) || 1;
  }

  /**
   * Sets the distributor installation quantity from the distributor installation quantity input
   * If no input is found, returns without setting quantity
   * @private
   */
  setDistributorInstallationQuantity() {
    if (
      !this.element.querySelector(
        '[data-distributor-installation-quantity] input'
      )
    )
      return;

    this.element.quantity =
      Number.parseInt(
        this.element.querySelector(
          '[data-distributor-installation-quantity] input'
        ).value
      ) || 1;
  }

  /**
   * Sets the self installation quantity from the self installation quantity input
   * If no input is found, returns without setting quantity
   * @private
   */
  setSelfInstallationQuantity() {
    if (!this.element.querySelector('[data-self-installation-quantity] input'))
      return;

    this.element.quantity =
      Number.parseInt(
        this.element.querySelector('[data-self-installation-quantity] input')
          .value
      ) || 1;
  }

  /**
   * Updates product prices based on the selected selling plan
   * @private
   */
  setCurrentSubscriptionPrice() {
    const checkedInput = this.element.querySelector(
      'input[name="selling_plan"]:checked'
    );

    for (const element of this.allProductPriceElements) {
      element.dataset.unitPrice = checkedInput.dataset.sellingPlanPrice;
    }

    updatePrice(this.element.quantity, this.productPriceElements);
    updatePrice(1, this.productUnitPriceElements);
  }

  /**
   * Resets prices to the initial variant price
   * @private
   */
  setInitialVariantPrice() {
    for (const element of this.allProductPriceElements) {
      element.dataset.unitPrice = element.dataset.oneTimePrice;
    }

    updatePrice(this.element.quantity, this.productPriceElements);
    updatePrice(1, this.productUnitPriceElements);
    updateFreshpackText(this.element, this.element.quantity);
  }

  /**
   * Handles UI updates when one-time purchase is selected
   * Updates quantities, prices and button states
   * @public
   */
  afterOneTimePurchaseSelected() {
    const selectedInput = this.element.querySelector(
      'input[name="selling_plan"]:checked'
    );

    this.addToCartButton.disabled = false;
    this.setOneTimeQuantity();
    this.setInitialVariantPrice();

    if (this.subscriptionCaption) {
      this.subscriptionCaption.classList.add('hidden');
    }

    if (!selectedInput) return;

    this.lastSellingPlan = selectedInput.value;
    this.element.querySelector('input[name="selling_plan"]:checked').checked =
      false;
  }

  /**
   * Handles UI updates when subscription purchase is selected
   * Updates quantities, prices and restores last selected subscription
   * @public
   */
  afterSubscriptionPurchaseSelected() {
    if (this.lastSellingPlan) {
      this.element.querySelector(
        `input[value="${this.lastSellingPlan}"]`
      ).checked = true;
    } else {
      this.addToCartButton.disabled = true;
    }

    this.setSubscriptionQuantity();
    this.setCurrentSubscriptionPrice();
    updatePrice(this.element.quantity, this.productPriceElements);
    updatePrice(1, this.productUnitPriceElements);
    updateFreshpackText(this.element, this.element.quantity);

    if (this.subscriptionCaption) {
      this.subscriptionCaption.classList.remove('hidden');
    }
  }

  /**
   * Handles UI updates when distributor installation is selected
   * Updates quantities and enables the add to cart button
   * @public
   */
  afterDistributorInstallationSelected() {
    this.setDistributorInstallationQuantity();
    if (this.addToCartButton) {
      this.addToCartButton.disabled = false;
      this.addToCartButton.classList.add('hidden');
    }

    if (this.distributorInstallationButton) {
      this.distributorInstallationButton.classList.remove('hidden');
    }
  }

  /**
   * Handles UI updates when self installation is selected
   * Updates quantities and manages add to cart button state based on confirmation
   * @public
   */
  afterSelfInstallationSelected() {
    this.setSelfInstallationQuantity();
    if (this.distributorInstallationButton) {
      this.distributorInstallationButton.classList.add('hidden');
    }

    if (this.addToCartButton) {
      this.addToCartButton.classList.remove('hidden');
      this.addToCartButton.disabled = true;

      if (this.selfInstallationConfirmationInput.checked) {
        this.addToCartButton.disabled = false;
      }
    }
  }
}
